<script setup lang="ts">
import { faCloudUploadAlt } from '@fortawesome/pro-regular-svg-icons';

/********************
 * PROPS & EMITS     *
 ********************/
export interface CiDropzoneProps {
  accept?: string;
  dropzonePending: boolean;
  hasError?: boolean;
  name: string;
  type?: 'single' | 'multiple';
}
const props = withDefaults(defineProps<CiDropzoneProps>(), {
  accept: '',
  hasError: false,
  type: 'single',
});

const emit = defineEmits<{
  'ci-dropzone-files-selected': [value: any];
}>();

/********************
 * COMPOSITIONS      *
 ********************/
const { $gettext } = useGettext();

/********************
 * REFS & VARS       *
 ********************/
const dragOver = ref(false);
const fileInputRef = ref<HTMLInputElement | null>(null);

/********************
 * HOOKS             *
 ********************/
onMounted(() => {
  window.addEventListener('drop', windowDragHandler);
  window.addEventListener('dragend', windowDragHandler);
  window.addEventListener('dragover', windowDragHandler);
});

onBeforeUnmount(() => {
  window.removeEventListener('drop', windowDragHandler);
  window.removeEventListener('dragend', windowDragHandler);
  window.removeEventListener('dragover', windowDragHandler);
});

/********************
 * FUNCTIONS         *
 ********************/
function windowDragHandler(event) {
  event.preventDefault();
}

function fileDropped(event: DragEvent) {
  event?.preventDefault();
  const files = [];

  if (event.dataTransfer) {
    for (let i = 0; i < Object.keys(event.dataTransfer.items).length; i += 1) {
      files.push(event.dataTransfer.items[i].getAsFile());
    }

    emit('ci-dropzone-files-selected', files);
    if (fileInputRef.value) {
      fileInputRef.value.value = '';
    }
  }
  dragOver.value = false;
}

function fileSelected() {
  const { files } = fileInputRef.value;
  emit('ci-dropzone-files-selected', files);

  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
}

function fileDragged(event) {
  event.preventDefault();
}

function onDragover(event) {
  event?.preventDefault();
  dragOver.value = true;
}

function onDragleave() {
  dragOver.value = false;
}

function triggerUpload() {
  fileInputRef.value?.click();
}
</script>

<template>
  <div
    ref="dropzone"
    :class="[
      { 'bg-primary/10': dragOver },
      props.dropzonePending ? 'cursor-progress' : 'cursor-pointer',
      props.hasError ? 'border-danger' : 'border-primary',
    ]"
    class="dropzone flex min-h-80 items-center justify-center overflow-hidden rounded-DEFAULT border border-dashed p-6"
    @dragover="evt => !props.dropzonePending && onDragover(evt)"
    @dragleave="onDragleave"
    @dragend="fileDragged"
    @drop="fileDropped"
    @click="!props.dropzonePending && triggerUpload()"
  >
    <div
      v-if="!props.dropzonePending"
      class="flex flex-col items-center"
    >
      <CiAwesomeIcon
        :icon="faCloudUploadAlt"
        class="flex w-full justify-center fill-dark"
        ratio="3"
      />
      <div class="flex w-full flex-col justify-center">
        <div class="text-medium text-center">
          {{ $gettext('Drag files here') }}
        </div>
      </div>
      <input
        v-if="props.type === 'single'"
        ref="fileInputRef"
        :name="props.name"
        :accept="props.accept"
        class="hidden overflow-hidden"
        type="file"
        single
        @change="fileSelected"
      />
      <input
        v-if="props.type === 'multiple'"
        ref="fileInputRef"
        :name="props.name"
        :accept="props.accept"
        class="hidden overflow-hidden"
        type="file"
        multiple
        @change="fileSelected"
      />
      <div class="dropzone__subline flex flex-row justify-center pb-0 lg:pb-4">
        <div class="inline">
          <span>{{ $gettext('Or just click') }}</span>
          <CiButton
            tag="button"
            variant="text"
            size="small"
            class="ml-1"
          >
            {{ $gettext('here') }}
          </CiButton>
        </div>
      </div>
      <slot />
    </div>

    <div
      v-else
      class="flex h-full flex-col items-center justify-center"
    >
      <CiSpinner size="lg" />
    </div>
  </div>
</template>
